import {
  EditorBackendResponse,
  EditorCenterSettings,
  EditorContext,
  EditorState,
  editorUiDefault,
} from '../contexts/Editor.context'
import { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import {
  ContentType,
  WebsiteConfigBM,
  WebsiteConfigPage,
} from '@bm-js/atlascloud-shared'
import {
  findContentByUuid,
  findPageByUuid,
  getBreadcrumbs,
  getEditorCenterSettings,
  parseNavTree,
  patchContentInPage,
  patchPageInConfig,
} from '../utils/editor.utils'
import { useEditorApi } from '../hooks/useEditorApi'
import { useBeforeUnload } from '../hooks/useBeforeUnload'
import { _omit } from '@naturalcycles/js-lib/object'
import { useKeyPress } from '../hooks/useKeyPress'
import { useWindowSize } from '../hooks/useWindowSize'
import { useFetchOnMount } from '../hooks/useFetchOnMount'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'

export const EditorProvider = ({ children }: PropsWithChildren) => {
  const [workingConfig, setWorkingConfig] = useState<WebsiteConfigBM>()
  const [activePageUuid, setActivePageUuid] = useState<string | null>(null)
  const [activeContentUuid, setActiveContentUuid] = useState<string | null>(
    null
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [editorUi, setEditorUi] = useState(editorUiDefault)

  const { saveConfig, publishConfig } = useEditorApi()
  const { tr } = useTr()
  const { id } = useParams()
  const windowSize = useWindowSize()

  const toggleLeftBar = useCallback(() => {
    setEditorUi((prev) => ({ ...prev, leftBarOpen: !prev.leftBarOpen }))
  }, [])

  const onSuccess = useCallback((data: EditorBackendResponse) => {
    setWorkingConfig(data.websiteConfig)
    setActivePageUuid(data.websiteConfig.pages[0]?.uuid || null)
  }, [])

  const {
    isError,
    isLoading,
    data: backendResponse,
    revalidate,
  } = useFetchOnMount<EditorBackendResponse>(`customer/websites/${id}`, {
    onSuccess,
  })

  const isSaved = useMemo(() => {
    if (!workingConfig || !backendResponse) return true
    return (
      JSON.stringify(workingConfig) ===
      JSON.stringify(backendResponse.websiteConfig)
    )
  }, [workingConfig, backendResponse])

  const exitFullScreen = () => {
    setEditorUi((prev) => ({ ...prev, fullScreen: null }))
  }

  useBeforeUnload(!isSaved, tr(TrKey.editorHaveYouSaved))
  useKeyPress({
    condition: !!editorUi.fullScreen,
    key: 'Escape',
    handler: exitFullScreen,
  })

  const navTree = useMemo(() => {
    if (!workingConfig) return []
    return workingConfig.pages.map(parseNavTree)
  }, [workingConfig])

  const activePage = useMemo<WebsiteConfigPage | null>(() => {
    return findPageByUuid(workingConfig?.pages, activePageUuid)
  }, [activePageUuid, workingConfig])

  const activeContent = useMemo<ContentType<unknown> | null>(() => {
    return findContentByUuid(activePage, activeContentUuid)
  }, [activeContentUuid, activePage])

  const breadcrumbs = useMemo(() => {
    return getBreadcrumbs(workingConfig?.pages, activePage)
  }, [workingConfig?.pages, activePage])

  const editorData = useMemo<EditorState['editorData']>(() => {
    if (!backendResponse || !workingConfig) return undefined
    return {
      backendResponse: _omit(backendResponse, ['websiteConfig']),
      navTree,
      workingConfig,
      activePage,
      activeContent,
      breadcrumbs,
    }
  }, [
    backendResponse,
    workingConfig,
    navTree,
    activePage,
    activeContent,
    breadcrumbs,
  ])

  const centerSettings = useMemo<EditorCenterSettings>(() => {
    return getEditorCenterSettings({
      ...editorUi,
      ...windowSize,
      rightBarOpen: !!activeContent,
    })
  }, [activeContent, editorUi, windowSize])

  const saveConfig_ = useCallback(async () => {
    if (!workingConfig) return
    setIsSaving(true)
    await saveConfig(workingConfig)
    await revalidate()
    setIsSaving(false)
  }, [workingConfig, saveConfig, revalidate])

  const publishConfig_ = useCallback(async () => {
    if (!workingConfig) return
    setIsPublishing(true)
    await publishConfig(workingConfig)
    await revalidate()
    setIsPublishing(false)
  }, [workingConfig, publishConfig, revalidate])

  const setActivePage = useCallback((pageUuid: string) => {
    setActivePageUuid(pageUuid)
  }, [])

  const setActiveContent = useCallback((contentUuid: string | null) => {
    setActiveContentUuid(contentUuid)
  }, [])

  const patchConfig = useCallback((updatedConfig: Partial<WebsiteConfigBM>) => {
    setWorkingConfig((prev) =>
      prev ? { ...prev, ...updatedConfig } : undefined
    )
  }, [])

  const patchActivePage = useCallback(
    (updatedPage: Partial<WebsiteConfigPage>) => {
      if (!activePage || !workingConfig) return
      const patchWithUuid = { ...updatedPage, uuid: activePage.uuid }
      const updated = patchPageInConfig(patchWithUuid, workingConfig)
      patchConfig({
        pages: updated.pages,
      })
    },
    [activePage, workingConfig, patchConfig]
  )

  const patchActiveContent = useCallback(
    (updatedContent: Partial<ContentType<unknown>>) => {
      if (!activePage) return
      const updatedPage = patchContentInPage(updatedContent, activePage)
      patchActivePage(updatedPage)
    },
    [activePage, patchActivePage]
  )
  return (
    <EditorContext.Provider
      value={{
        editorData,
        actions: {
          setUi: setEditorUi,
          saveConfig: saveConfig_,
          publishConfig: publishConfig_,
          setActivePage,
          setActiveContent,
          patchConfig,
          patchActivePage,
          patchActiveContent,
          toggleLeftBar,
        },
        status: {
          isSaved,
          isLoading,
          isError,
          isSaving,
          isPublishing,
          isPublished: !!backendResponse?.isPublished,
        },
        ui: editorUi,
        centerSettings,
      }}
    >
      {children}
    </EditorContext.Provider>
  )
}
